import dayjs from 'dayjs';
import i18next from 'i18next';
import { ArrayParam, NumberParam, NumericArrayParam, StringParam, withDefault } from 'use-query-params';

import {
  image1,
  image1Small,
  image2,
  image2Small,
  image3,
  image3Small,
  image4,
  image4Small,
  image5,
  image5Small
} from './images';

export const dateRanges = {
  Bugun: [dayjs(), dayjs()],
  Kecha: [dayjs().add(-1, 'day').startOf('day'), dayjs()],
  'Joriy hafta': [dayjs().startOf('week').add(1, 'day'), dayjs().endOf('week').add(1, 'day')],
  'Joriy oy': [dayjs().startOf('month'), dayjs().endOf('month')],
  "O'tkan oy": [dayjs().add(-1, 'month').startOf('month'), dayjs().add(-1, 'month').endOf('month')],
  'Joriy yil': [dayjs().startOf('year'), dayjs().endOf('year')],
  "O'tkan yil": [dayjs().add(-1, 'year').startOf('year'), dayjs().add(-1, 'year').endOf('year')]
};

export const images = [
  {
    id: 0,
    value: undefined,
    smallValue: undefined
  },
  {
    id: 1,
    value: image1,
    smallValue: image1Small
  },
  {
    id: 2,
    value: image5,
    smallValue: image5Small
  },
  {
    id: 3,
    value: image4,
    smallValue: image4Small
  },
  {
    id: 4,
    value: image2,
    smallValue: image2Small
  },
  {
    id: 5,
    value: image3,
    smallValue: image3Small
  }
];

export const queryKeys = {
  DASHBOARD_WIDGETS: 'dashboard-v2-widgets',
  ALL_DASHBOARDS: 'dashboard-v2-all',
  DASHBOARD_ID: "'dashboard-v2-id'",
  DASHBOARD_WIDGET_STATIC_CALL_HISTORY_ID: 'DASHBOARD_WIDGET_STATIC_CALL_HISTORY_ID'
};

export const defaultSize: Record<
  string,
  {
    minW: number;
    minH: number;
    maxW?: number;
    maxH?: number;
  }
> = {
  NUMBER: {
    minW: 1,
    minH: 1,
    maxH: 1,
    maxW: 1
  },
  LIST: {
    minW: 3,
    minH: 2,
    maxH: 2,
    maxW: 3
  },
  PIE_CHART: {
    minW: 3,
    minH: 2,
    maxW: 3,
    maxH: 2
  },
  CHART: {
    minW: 3,
    minH: 2,
    maxW: 3,
    maxH: 2
  },
  TIMELINE: {
    minW: 3,
    minH: 2,
    maxH: 2,
    maxW: 3
  },
  STATIC: {
    minW: 3,
    minH: 2,
    maxW: 3,
    maxH: 2
  },
  DEFAULT: {
    minW: 1,
    minH: 2
  }
};

export const sizes: Record<
  string,
  {
    minW: number;
    minH: number;
    maxW: number;
    maxH: number;
  }
> = {
  NUMBER: {
    minW: 1,
    minH: 1,
    maxW: 1,
    maxH: 1
  },
  LIST: {
    minW: 3,
    minH: 2,
    maxW: 3,
    maxH: 2
  },
  PIE_CHART: {
    minW: 3,
    minH: 2,
    maxW: 3,
    maxH: 2
  },
  CHART: {
    minW: 3,
    minH: 2,
    maxW: 3,
    maxH: 2
  },
  TIMELINE: {
    minW: 3,
    minH: 2,
    maxW: 3,
    maxH: 2
  },
  STATIC: {
    minW: 3,
    minH: 2,
    maxW: 3,
    maxH: 2
  },
  DEFAULT: {
    minW: 1,
    minH: 2,
    maxW: 3,
    maxH: 2
  }
};

export const leadInitialColumn = [
  { id: '1', column: 'name' },
  { id: '2', column: 'contactName' },
  { id: '3', column: 'createdDate' },
  { id: '4', column: 'fullName' },
  { id: '5', column: 'pipeStatusName' }
].map(({ column }) => column);

export const taskInitialColumn = [
  { id: '1', column: 'clientName' },
  { id: '2', column: 'date' },
  { id: '3', column: 'leadTaskType' },
  { id: '4', column: 'text' },
  { id: '5', column: 'state' },
  { id: '6', column: 'assignedBy' }
].map(({ column }) => column);

export const eventListInitialColumn = [
  { id: '1', column: 'name' },
  { id: '2', column: 'createdDate' },
  { id: '3', column: 'actionType' },
  { id: '4', column: 'previous' },
  { id: '5', column: 'current' },
  { id: '6', column: 'responsibleBy' }
].map(({ column }) => column);

export const paymentListInitialColumn = [
  'number',
  'base',
  'clientFullName',
  'contractNumber',
  'createDate',
  'amount',
  'type',
  'createdBy'
];

export const contractInitialColumns = [
  {
    id: '1',
    column: 'number'
  },
  {
    id: '2',
    column: 'clientName'
  },
  {
    id: '3',
    column: 'createdDate'
  },
  {
    id: '4',
    column: 'pricePerArea'
  },
  {
    id: '5',
    column: 'amount'
  },
  {
    id: '6',
    column: 'flatNumber'
  },
  {
    id: '7',
    column: 'discountPercent'
  },
  {
    id: '8',
    column: 'status'
  },
  {
    id: '10',
    column: 'responsibleBy'
  },
  {
    id: '11',
    column: 'houseName'
  }
].map(({ column }) => column);

export const allColumns: Record<string, string[]> = {
  LEAD: leadInitialColumn,
  TASK: taskInitialColumn,
  CONTRACT: contractInitialColumns,
  DEFAULT: eventListInitialColumn,
  PAYMENT: paymentListInitialColumn,
  LOG_LEAD: leadInitialColumn
};

export const acceptTooltipKeys: Record<string, boolean> = {
  flatNumber: true,
  flatFloor: true,
  flatRooms: true
};

export const basicTitles: Record<string, string> = {
  current: `${i18next.t(`crm.previous`)}`,
  previous: `${i18next.t(`crm.current`)}`
};

export const dashboardFilterParams = {
  startDate: StringParam,
  finishDate: StringParam,
  responsibleByIds: NumericArrayParam
};

export const defaultEventListParamsMap = {
  startDate: StringParam,
  finishDate: StringParam,
  objectType: StringParam,
  objectStartDate: StringParam,
  objectFinishDate: StringParam,
  responsibleByIds: NumericArrayParam,
  actionTypes: ArrayParam,
  current: StringParam,
  previous: StringParam,
  currentStatuses: ArrayParam,
  previousStatuses: ArrayParam,
  currentTags: ArrayParam,
  previousTags: ArrayParam,
  clientIds: NumericArrayParam,
  search: StringParam,
  fields: ArrayParam,
  page: withDefault(NumberParam, 1),
  size: withDefault(NumberParam, 20)
};

export const secondEventListParamsMap = Object.keys(defaultEventListParamsMap).reduce(
  (pre, cur) => ({
    ...pre,
    [`two${cur.charAt(0).toUpperCase() + cur.slice(1)}`]:
      defaultEventListParamsMap[cur as keyof typeof defaultEventListParamsMap]
  }),
  {} as {
    [K in keyof typeof defaultEventListParamsMap as `two${Capitalize<K>}`]: (typeof defaultEventListParamsMap)[K];
  }
);

export const allDashboardTableColumns: Record<string, Array<any>> = {
  task: taskInitialColumn,
  contract: contractInitialColumns,
  'event-list': eventListInitialColumn,
  event_list: eventListInitialColumn,
  log: eventListInitialColumn,
  payment: paymentListInitialColumn,
  default: leadInitialColumn
};

export const paramsObjectType: Record<string, string> = {
  crm: 'LEAD',
  task: 'TASK',
  default: 'LOG',
  contract: 'CONTRACT',
  payment: 'PAYMENT',
  log_lead: 'LOG_LEAD'
};

export const objectTypesParam: Record<string, string> = {
  LEAD: 'crm',
  TASK: 'task',
  PAYMENT: 'payment',
  DEFAULT: 'event-list',
  CONTRACT: 'contract',
  LOG_LEAD: 'log_lead'
};

export const defs = [
  {
    colors: [
      {
        color: 'inherit',
        offset: 0,
        opacity: 0.6
      },
      {
        color: 'inherit',
        offset: 100,
        opacity: 0
      }
    ],
    id: 'gradient',
    type: 'linearGradient'
  }
];

export const fill = [
  {
    id: 'gradient',
    match: '*'
  }
];
